"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dna, ArrowRight, FlaskConical, Activity } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BespokePeptideSynthesis() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223191" id="ministry-speaking" className="bg-background text-foreground relative min-h-[90vh] flex items-center justify-center overflow-hidden border-b border-border/50">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/dustin_speaking_lectern.png')] bg-cover bg-center opacity-40 mix-blend-multiply transform scale-105 animate-[pulse_10s_ease-in-out_infinite]" aria-hidden="true" />
        
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-primary/80 to-primary" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/40 via-transparent to-primary/40" />
        
        {}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/5 rounded-lg blur-[120px] mix-blend-screen" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/10 rounded-lg blur-[100px] mix-blend-screen" />
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 py-24 sm:py-32 flex flex-col items-center text-center">
        
        {}
        <div ref={headerRef} className={`transition-all duration-1000 ease-out transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent/20 backdrop-blur-md border border-accent/30 text-primary-foreground text-sm font-medium mb-8 uppercase tracking-[0.2em] shadow-md">
            <Activity className="w-4 h-4 text-accent" />
            <span className="font-default">Ministry & Speaking Engagements</span>
          </div>
        </div>

        {}
        <div ref={textRef} className={`transition-all duration-1000 delay-200 ease-out transform max-w-5xl ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-default font-light tracking-tighter mb-6 leading-[1.1] text-primary-foreground">
            Equipping the <br className="hidden md:block" />
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-accent via-white to-accent bg-[length:200%_auto] animate-[pulse_4s_ease-in-out_infinite]">
              Local Church
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-12 font-default font-light leading-relaxed">
            Dustin Royer partners with congregations to unlock the transformative power of the Word. Through deep biblical study and practical application, we build lives rooted in eternal purpose.
          </p>
        </div>

        {}
        <div ref={ctaRef} className={`transition-all duration-1000 delay-400 ease-out transform flex flex-col items-center w-full ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col sm:flex-row gap-6 mb-16">
            <Button asChild size="lg" className="h-16 px-10 text-lg rounded-md bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg hover:shadow-xl transition-all duration-300 border border-accent/50">
              <Link to="/ContactPage">
                Download Speaking Kit <ArrowRight className="ml-3 w-5 h-5" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="h-16 px-10 text-lg rounded-md bg-white/5 backdrop-blur-sm border-white/20 text-primary-foreground hover:bg-white/10 hover:text-white transition-all duration-300">
              <Link to="/TheAuthorPage">
                About the Author
              </Link>
            </Button>
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-4xl pt-12 border-t border-white/10">
            {[{
            icon: Dna,
            title: "Biblical Depth",
            desc: "Theologically sound teaching"
          }, {
            icon: FlaskConical,
            title: "Practical Faith",
            desc: "Resources for daily living"
          }, {
            icon: Activity,
            title: "Church Focused",
            desc: "Supporting local leadership"
          }].map((feature, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-md bg-white/5 flex items-center justify-center border border-white/10 mb-2">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-default font-medium text-primary-foreground tracking-wide">{feature.title}</h3>
                <p className="text-sm text-primary-foreground/60 font-default">{feature.desc}</p>
              </div>)}
          </div>
        </div>

      </div>
    </section>;
}